import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { DropdownInputComponent } from "../../../shared/dropdown-input/dropdown-input.component";
import { TrialService } from "../../../core/services/trial.service";
import { LogHelper } from "../../../core/helpers/log.helper";
import { AlertService } from "../../../shared/alert/alert.service";
import { ChartItem } from "../../../shared/donut-chart/chart-item.model";
import { TemplateService } from "../../../core/services/template.service";
import { TrialReport } from "../../../core/models/trial-report.model";
import { StringHelper } from "../../../core/helpers/string-helper";
import { fromBeforeToDateValidator } from 'app/core/validators/from-before-to-date.validator';
import { DateHelper } from "../../../core/helpers/date-helper";
import { BrandService } from "../../../core/services/brand.service";
import { Brand } from "../../../core/models/brand.model";
import { TrialListItem } from "../../../core/models/trial-list.model";

@Component({
  selector: 'app-trial-report',
  templateUrl: './trial-report.component.html',
  styleUrls: ['./trial-report.component.scss']
})
export class TrialReportComponent implements OnInit {
  @ViewChild('brandSelect') brandSelect: DropdownInputComponent;
  @ViewChild('studySelect') studySelect: DropdownInputComponent;
  @ViewChild('fromDateInput') fromDateInput: ElementRef;
  @ViewChild('toDateInput') toDateInput: ElementRef;

  brandList: { value: string, text: string }[] = [];
  trialList: { value: string, text: string }[] = [];
  allTrials: TrialListItem[] = [];
  brands: Brand[] = [];
  chartData: ChartItem[] = [];
  platformData: ChartItem[] = [];
  report = new TrialReport();
  reportLoaded = false;
  selectedTrials: string[] = [];
  trialCount = 0;
  hasTravelSplit = false;
  isFormProcessing = false;
  datePickerDisabled = true;
  lastMonthDisabled = true;
  last30DaysDisabled = true;
  colours: string[] = ['#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F', '#EDC948'];

  filterForm: UntypedFormGroup;
  showViewAllBrandsBtn = false;
  showViewAllBtn = false;

  allTimeToDate = DateHelper.addDaysToDate(new Date(), 365);
  last30DaysStartDate = DateHelper.addDaysToDate(new Date(), -30);

  constructor(
    private templateService: TemplateService,
    private trialService: TrialService,
    private brandService: BrandService,
    private alertService: AlertService) {
  }

  ngOnInit(): void {
    this.templateService.showHeader();

    this.filterForm = new UntypedFormGroup({
      brandId: new UntypedFormControl(),
      trialId: new UntypedFormControl(),
      from: new UntypedFormControl({ value: this.getDateStr(this.last30DaysStartDate), disabled: true }),
      to: new UntypedFormControl({ value: this.getDateStr(new Date()), disabled: true }),
      allTime: new UntypedFormControl(false),
      lastMonth: new UntypedFormControl(false),
      last30Days: new UntypedFormControl(true),
    }, { validators: fromBeforeToDateValidator }
    );

    this.filterForm.get('brandId').valueChanges.subscribe(brandId => {
      if (brandId !== null && brandId !== '') {
        this.showViewAllBrandsBtn = true;
      } else {
        this.showViewAllBrandsBtn = false;
        this.brandSelect?.reset();
      }

      this.updateTrialListByBrand(brandId);
    });

    this.filterForm.get('trialId').valueChanges.subscribe(trialId => {
      if (trialId !== null && trialId !== '') {
        this.showViewAllBtn = true;
      } else {
        this.showViewAllBtn = false;
        this.studySelect?.reset();
      }
    });

    this.filterForm.get('allTime').valueChanges.subscribe(value => {
      if (value) {
        this.filterForm.patchValue({
          lastMonth: false,
          last30Days: false
        })
        this.filterForm.patchValue({
          from: this.getDateStr(new Date(2020, 0)),
          to: this.getDateStr(this.allTimeToDate)
        });
        this.filterForm.get('from').disable();
        this.filterForm.get('to').disable();
      } else {
        this.filterForm.get('from').enable();
        this.filterForm.get('to').enable();
      }
    });

    this.filterForm.get('last30Days').valueChanges.subscribe(value => {
      if (value) {
        let fromDate = new Date()
        fromDate.setDate(fromDate.getDate() - 30)
        let toDate = new Date()

        this.filterForm.patchValue({
          allTime: false,
          lastMonth: false,
          from: fromDate.toLocaleDateString("en-GB"),
          to: toDate.toLocaleDateString("en-GB")
        });

        this.filterForm.get('from').disable();
        this.filterForm.get('to').disable();
      } else {
        this.filterForm.get('from').enable();
        this.filterForm.get('to').enable();
      }
    })

    this.filterForm.get('lastMonth').valueChanges.subscribe(value => {
      if (value) {
        let fromDate = new Date()
        fromDate.setDate(1);
        fromDate.setHours(-1);
        fromDate.setDate(1);
        let toDate = new Date()
        toDate.setDate(1);
        toDate.setHours(-1);

        this.filterForm.patchValue({
          allTime: false,
          last30Days: false,
          from: fromDate.toLocaleDateString("en-GB"),
          to: toDate.toLocaleDateString("en-GB")
        });
        this.filterForm.get('from').disable();
        this.filterForm.get('to').disable();
      } else {
        this.filterForm.get('from').enable();
        this.filterForm.get('to').enable();
      }
    })

    this.loadBrands();
    this.loadTrials();
    this.onFormSubmit();
  }

  onMouseEnterForm() {
    if (!this.filterForm.get('allTime').value) {
      if (this.fromDateInput.nativeElement.value != this.filterForm.get('from')) {
        this.filterForm.patchValue({ from: this.fromDateInput.nativeElement.value });
      }

      if (this.toDateInput.nativeElement.value != this.filterForm.get('to')) {
        this.filterForm.patchValue({ to: this.toDateInput.nativeElement.value });
      }
    }
  }

  loadReport(trialIds: string[], from: string, to: string) {
    this.trialService.report(trialIds, from, to).subscribe({
      next: (report: any) => {
        this.report = report;

        this.chartData = [];
        this.hasTravelSplit = false;

        if (report.carTrips > 0 || report.planTrips > 0 || report.trainTrips > 0) {
          this.chartData.push(new ChartItem('Car', report.carPercent, report.carPercent + '%', '#D44618'));
          this.chartData.push(new ChartItem('Plane', report.planePercent, report.planePercent + '%', '#fec000'));
          this.chartData.push(new ChartItem('Train', report.trainPercent, report.trainPercent + '%', '#00b958'));
          this.hasTravelSplit = true;
        }

        this.selectedTrials = report.trialIds;
        this.trialCount = report.trialIds.length;

        this.platformData = [];
        let i = 0;
        report.platforms.forEach(p => {
          this.platformData.push(new ChartItem(p.platform, p.percent, p.count, this.colours[i] ? this.colours[i] : '#ff9900'));
          i++;
          if (i >= this.colours.length) {
            i = 0;
          }
        });

        this.reportLoaded = true;
        this.isFormProcessing = false;
      },
      error: (error) => {
        LogHelper.log(error);
        this.isFormProcessing = false;
      }
    });
  }

  onViewAll() {
    this.filterForm.patchValue({ trialId: '' });
  }

  onViewAllBrands() {
    this.filterForm.patchValue({ brandId: '' });
  }

  loadBrands() {
    this.brandService.getBrands().subscribe({
      next: brands => {
        this.brands = brands;
        this.brandList = brands.map(brand => ({ value: brand.id, text: brand.name }));
      },
      error: (error) => {
        this.alertService.showWarningAlert("Unable to load list of brands");
      }
    });
  }

  loadTrials() {
    this.trialService.retrieveTrials(1, null, 9999).subscribe({
      next: (trials: any) => {
        this.allTrials = trials.results;
        this.updateTrialListByBrand(this.filterForm.get('brandId').value);
      },
      error: (error) => {
        this.alertService.showWarningAlert("Unable to load list of trials");
      }
    });
  }

  updateTrialListByBrand(brandId: string) {
    let filteredTrials = this.filterTrialsByBrand(brandId);
    filteredTrials.sort((a, b) => a.code.localeCompare(b.code));

    this.trialList = filteredTrials.map(trial => ({ value: trial.id, text: StringHelper.limit(trial.label, 30) }));

    const selectedTrialId = this.filterForm.get('trialId').value;
    if (selectedTrialId && !filteredTrials.some(t => t.id === selectedTrialId)) {
      this.filterForm.patchValue({ trialId: '' });
    }
  }

  filterTrialsByBrand(brandId: string): TrialListItem[] {
    if (brandId === null || brandId === '') {
      return [...this.allTrials];
    }

    const selectedBrand = this.brands.find(b => b.id === brandId);
    if (!selectedBrand) {
      return [];
    }

    return this.allTrials.filter(t => t.brandName === selectedBrand.name);
  }

  getDateStr(date: Date) {
    return (`00${date.getDate()}`).slice(-2) + '/' + (`00${+date.getMonth() + 1}`).slice(-2) + '/' + date.getFullYear();
  }

  onFormSubmit() {
    if (this.filterForm.invalid) {
      return;
    }

    const { brandId, trialId, from, to } = this.filterForm.getRawValue();

    this.isFormProcessing = true;

    if (trialId) {
      this.loadReport([trialId], from, to);
    } else if (brandId) {
      const trialIds = this.filterTrialsByBrand(brandId).map(t => t.id);

      if (trialIds.length > 0) {
        this.loadReport(trialIds, from, to);
      } else {
        this.clearReport();
      }
    } else {
      this.loadReport([], from, to);
    }
  }

  private clearReport() {
    this.report = new TrialReport();
    this.selectedTrials = [];
    this.trialCount = 0;
    this.chartData = [];
    this.platformData = [];
    this.hasTravelSplit = false;
    this.reportLoaded = true;
    this.isFormProcessing = false;
  }
}
